import {
  FormEvent,
  memo,
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useRef
} from 'react'
import { toJS } from 'mobx'
import {
  NavigateFunction,
  Route,
  Routes as ReactRouterRoutes
} from 'react-router-dom'
import { Form } from 'react-final-form'
import { FormSubscription } from 'final-form'
import { TurnstileInstance } from '@marsidev/react-turnstile'

import TemporarySkeleton from 'components/SkeletonView/TemporarySkeleton'
import CategoryLayout from 'components/Layout/Category'
import { trackInAmplitude } from 'utils/tracking/amplitude'
import { FooterPortalContainer } from 'components/Portal/Footer'
import ReturningVisitor from 'components/ReturningVisitor'
import { useClearError } from 'context/Error/hook'
import { getDisplayedPages } from 'config/utils/config'
import useStore from 'stores/utils/hooks/useStore'
import InitialSkeleton from 'components/SkeletonView/InitialSkeleton'
import { AmplitudeEvent } from 'utils/tracking/amplitude/amplitude.interface'
import PageLayout from 'components/Layout/Page'
import ProgressIndicator from 'components/ProgressIndicator'
import QueryParamsUpdater from 'containers/QueryParamsUpdater'
import { RegisteredEventName } from '@finanzcheck/catalyst-pollard'
import pollard from 'utils/pollard'
import InitialSmavaDDFSkeleton from 'components/SkeletonView/InitialSmavaDDFSkeleton'
import isNotAffiliate from 'utils/formDependencies/conditions/isNotAffiliate'
import { FormNavigationSwitcher } from 'components/FormNavigation'
import InactivityContainer from 'containers/InactivityContainer'
import { DacFooterWrapper } from 'components/DigitalAccountCheck/DacPageComponents/DacFooterWrapper'
import Agb from 'components/RceEdit/AGB'
import { CaptchaWidget } from 'components/CaptchaWidget'
import { useCaptcha } from 'hooks/useCaptcha'
import FooterManager from 'components/Footer/FooterManager'
import RouteElement from 'components/Route/RouteElement'
import { useFormSubmission } from 'hooks/useFormSubmission'
import { useLayoutFlags } from 'hooks/useLayoutFlags'
import { usePageTracking } from 'hooks/usePageTracking'
import { useAgeGroup } from 'stores/utils/hooks/useAgeGroup'
import { AgbConsentParagraph } from 'components/AGB'

const formSubscriptions: FormSubscription = {
  submitting: true,
  pristine: true
}

type RoutesProps = {
  navigate: NavigateFunction
}

const Routes = ({ navigate }: PropsWithChildren<RoutesProps>) => {
  const {
    rootStore,
    urlParams,
    traversal,
    captcha,
    config,
    isLastDisplayedPage,
    goToPreviousPage
  } = useStore((store) => ({
    rootStore: store,
    urlParams: store.urlParams,
    traversal: store.traversal,
    captcha: store.captcha,
    config: store.page.config,
    isLastDisplayedPage: store.page.isLastDisplayedPage,
    goToPreviousPage: store.navigation.goToPreviousPage
  }))

  const ageGroup = useAgeGroup()
  const clearError = useClearError()

  const captchaRef = useRef<TurnstileInstance | null>(null)
  const { handleCaptcha, setToken, shouldAwaitCaptcha, shouldHandleCaptcha } =
    useCaptcha({
      captchaRef
    })

  // Page tracking hook handles all page tracking logic
  const { currentPage } = usePageTracking(() => {
    // Clear errors when navigating to a new page
    clearError()
  })

  // Use the form submission hook instead of the large onSubmit function
  const { handleSubmit: formSubmissionHandler } = useFormSubmission(
    navigate,
    currentPage,
    {
      handleCaptcha: (action: string) => handleCaptcha(action as any),
      shouldHandleCaptcha: (action: string) =>
        shouldHandleCaptcha(action as any),
      shouldAwaitCaptcha
    }
  )

  // Use layout flags hook to calculate display conditions
  const {
    isProgressIndicatorDisplayed,
    isAgbShownAtBottomOfPage,
    isDacFooterWrapperDisplayed,
    isSmava
  } = useLayoutFlags(currentPage || undefined, config.name, ageGroup)

  const isLastPage = isLastDisplayedPage(currentPage || '')

  const parentFrameStartLoadTimeCallback = (timestamp: number) => {
    trackInAmplitude(AmplitudeEvent.ParentFrameStartLoadTime, {
      timestamp
    })
  }

  useEffect(() => {
    pollard.listenTo(
      RegisteredEventName.parentFrameStartLoadDate,
      parentFrameStartLoadTimeCallback
    )
  }, [])

  const handleCaptchaWidgetLoad = useCallback(() => {
    captcha.setCaptchaFlowStatus('loaded')
    captcha.setCaptchaRef(captchaRef)
  }, [captcha])

  const preventSubmit = useCallback((e: FormEvent) => {
    e.preventDefault()
  }, [])

  const initialValues = useMemo(() => toJS(traversal.data), [traversal.data])

  const isNotAffiliateBoolean = isNotAffiliate(urlParams?.context)

  return (
    <>
      <CaptchaWidget
        ref={captchaRef}
        onWidgetLoad={handleCaptchaWidgetLoad}
        onSuccess={setToken}
      />
      {!config.version || !currentPage ? (
        isSmava ? (
          <InitialSmavaDDFSkeleton />
        ) : (
          <InitialSkeleton />
        )
      ) : (
        <>
          {isNotAffiliateBoolean && isSmava && <QueryParamsUpdater />}
          <Form
            subscription={formSubscriptions}
            initialValues={initialValues}
            validateOnBlur
            // Form submission handled by useFormSubmission hook
            onSubmit={formSubmissionHandler as any}
          >
            {({ handleSubmit, form }) => {
              traversal.setFormApi(form)

              return (
                <PageLayout onSubmit={preventSubmit}>
                  {isProgressIndicatorDisplayed && (
                    <ProgressIndicator
                      withMotivationalText={!isSmava}
                      isSlimProgress={isSmava}
                      isWithMotivationalTextBelow={isSmava}
                    />
                  )}
                  <ReturningVisitor />
                  <ReactRouterRoutes>
                    {getDisplayedPages(rootStore).map(([pageName, options]) => (
                      <Route
                        key={pageName}
                        path={pageName}
                        element={
                          // `key` here (not just on <Route>) forces a full
                          // remount per page: pages that share component
                          // names (e.g. `personal_primary`) would otherwise
                          // let React reconcile them as the same element
                          // across a route change instead of unmounting.
                          <RouteElement
                            key={pageName}
                            options={options}
                            navigate={navigate}
                          />
                        }
                      />
                    ))}
                    <Route path="*" element={<TemporarySkeleton />} />
                  </ReactRouterRoutes>
                  {isLastPage && (
                    <InactivityContainer
                      handleSubmit={handleSubmit}
                      isSmava={isSmava}
                    />
                  )}

                  <FormNavigationSwitcher
                    onNext={handleSubmit}
                    onBack={() => goToPreviousPage(navigate)}
                    navigate={navigate}
                  />

                  {isDacFooterWrapperDisplayed && (
                    <CategoryLayout>
                      <DacFooterWrapper isFfgDacWrapper={!isSmava} />
                    </CategoryLayout>
                  )}

                  <FooterPortalContainer />
                  {isAgbShownAtBottomOfPage ? (
                    isSmava ? (
                      <Agb />
                    ) : (
                      <CategoryLayout>
                        <AgbConsentParagraph boldLinks />
                      </CategoryLayout>
                    )
                  ) : null}
                </PageLayout>
              )
            }}
          </Form>
          <FooterManager
            isNotAffiliate={isNotAffiliateBoolean}
            currentPageInSession={currentPage || ''}
            config={config}
          />
        </>
      )}
    </>
  )
}

export default memo(Routes)
